/**
 * 全局提示弹框（含「请重新登录」框）的上报。
 *
 * 这类弹框会把用户直接赶回登录页，是线上投诉最集中的一类交互，但它的触发点分散在主站与
 * BRM 两套请求链路的多个错误码分支里，只凭用户截图无法还原「这次到底是谁弹的」。本模块
 * 在弹框真正展示时，把链路、场景、错误码、后端原始文案、触发接口，以及「请求所带 token
 * 是否仍是本地最新 token」一并上报到 SLS，便于按场景聚合定位。
 *
 * 其中 requestTokenIsLatest 为 false 即可直接判定为「老页面拿着旧 token 请求」，
 * 是排查多标签页重新登录后被反复引导登录的关键线索。
 *
 * @module utils/globalAlertTrack
 */
import { LOCAL_STORAGE_KEY, LOGIN_INVALID_CODES } from '@/constants';
import { getStorageAuthInfo, checkLogin } from '@/utils/storageOperateApi';
import slsLogger from '@/utils/slsLogger';

/** slsLogger 组件名 */
const LOG_COMPONENT = 'GlobalAlertDialog';
/** token 只上报尾段用于比对，避免完整凭证进日志 */
const TOKEN_TAIL_LENGTH = 8;

/** 弹框来自哪条请求链路 */
export const ALERT_CHANNEL = {
  /** 主站 / 进门链路（src/jmHttp） */
  MAIN: 'jmHttp',
  /** BRM 链路（src/apis） */
  BRM: 'brmApi',
  /** SSE 流式链路 */
  SSE: 'sse'
};

/** 弹框的触发场景，便于在 SLS 里按来源聚合 */
export const ALERT_SCENE = {
  /** 接口以 HTTP 错误或业务错误码返回 */
  HTTP_ERROR: 'http-error',
  /** 连续重试后仍拿到失效码的兜底提示 */
  HTTP_RETRY_LIMIT: 'http-retry-limit',
  /** HTTP 200 但响应体业务码非 0 */
  RESPONSE_BODY: 'response-body',
  /** 刷新 access_token 失败 */
  TOKEN_REFRESH_FAILED: 'token-refresh-failed',
  /** SSE 流内返回失效码 */
  SSE_STREAM: 'sse-stream'
};

/**
 * 只保留 token 尾段，用于跨日志比对而不泄露完整凭证。
 * @param {string} token - 原始 token
 * @returns {string} 形如 `...abcd1234` 的尾段，无 token 时返回空串
 */
function maskToken(token) {
  if (!token) return '';
  return `...${String(token).slice(-TOKEN_TAIL_LENGTH)}`;
}

/**
 * 提取本次请求实际携带的 token。
 * @param {object} [config] - axios 请求配置
 * @returns {string} 请求携带的 token，取不到返回空串
 */
function readRequestToken(config) {
  const headers = config?.headers || {};
  const authorization = headers.Authorization || headers.authorization || '';
  // BRM 链路的 Authorization 形如 "Bearer xxx"，只取凭证部分
  if (authorization) return String(authorization).split(' ').pop();
  // 主站链路直接放在 token / web_token 头上
  return headers.token || headers.web_token || '';
}

/**
 * 提取可读的接口标识。
 * @param {object} [config] - axios 请求配置
 * @returns {string} 主站链路返回 app_mod_act，其余返回请求 url
 */
function readApiName(config) {
  const { app, mod, act } = config?.headers || {};
  // 主站接口按 app_mod_act 定位比 url 更直观
  if (app || mod || act) return `${app || ''}_${mod || ''}_${act || ''}`;
  return config?.url || '';
}

/**
 * 读取本地当前有效的 token，用于和请求实际携带的 token 比对。
 * @param {string} channel - 弹框来源链路，取值见 ALERT_CHANNEL
 * @returns {string} 本地 token，未登录时为空串
 */
function readLocalToken(channel) {
  // BRM 链路用 keycloak 换来的 access_token，其余链路用进门的 webtoken
  if (channel === ALERT_CHANNEL.BRM) {
    const { brmToken } = getStorageAuthInfo() || {};
    return brmToken?.access_token || brmToken?.accessToken || brmToken?.token || '';
  }
  const jmAuthInfo = getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO) || {};
  return jmAuthInfo.webtoken || jmAuthInfo.token || '';
}

/**
 * 上报一次全局提示弹框的展示，须在弹框真正展示时调用。
 * @param {object} params - 上报参数
 * @param {string} params.channel - 弹框来源链路，取值见 ALERT_CHANNEL
 * @param {string} [params.scene] - 触发场景，取值见 ALERT_SCENE
 * @param {number|string} [params.code] - 触发弹框的错误码
 * @param {string} [params.message] - 弹框展示给用户的文案
 * @param {object} [params.config] - 触发弹框的 axios 请求配置
 * @param {object} [params.extra] - 额外的排查字段
 * @returns {void}
 * @example
 * trackGlobalAlert({ channel: ALERT_CHANNEL.MAIN, scene: ALERT_SCENE.HTTP_ERROR, code: 201, message, config });
 */
export function trackGlobalAlert({ channel, scene = '', code, message = '', config = null, extra = {} }) {
  try {
    const codeText = String(code ?? '');
    const isLoginInvalid = LOGIN_INVALID_CODES.has(codeText);
    const jmAuthInfo = getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO) || {};
    const requestToken = readRequestToken(config);
    const localToken = readLocalToken(channel);

    slsLogger.console({
      component: LOG_COMPONENT,
      level: 'error',
      message: isLoginInvalid ? '登录失效弹框-弹出' : '全局错误弹框-弹出',
      extra: {
        channel,
        scene,
        code: codeText,
        alertMessage: message,
        api: readApiName(config),
        url: config?.url || '',
        path: `${window.location.pathname}${window.location.search}`,
        uid: jmAuthInfo.uid || '',
        requestToken: maskToken(requestToken),
        localToken: maskToken(localToken),
        // false 说明这次请求带的是老页面里的旧 token，本地其实已经换成新的了
        requestTokenIsLatest: !requestToken || !localToken || requestToken === localToken,
        // false 说明弹框时本机已经没有完整登录态（例如换发空窗期或已被登出）
        hasLocalLogin: checkLogin(),
        ...extra
      }
    });
  } catch (error) {
    // 上报失败不能影响弹框本身
    console.error('[GlobalAlertDialog] 弹框上报失败', error);
  }
}
